// src/views/BackOffice/ManageBMOs.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Alert,
  Spinner,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col
} from 'reactstrap';
import axios from '../../api/Axios';

const ManageBMOs = () => {
  const [bmos, setBmos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBMO, setSelectedBMO] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBMOs();
  }, []);

  const fetchBMOs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('accounts/bmo/list/');
      console.log('BMO API Response:', response.data); // Debug log
      
      // Debug individual BMOs - show all fields
      response.data.forEach((bmo, index) => {
        console.log(`BMO ${index} - All fields:`, bmo);
      });
      
      setBmos(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching BMOs:', error);
      setError('Failed to load BMO list. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBMO = (bmo) => {
    setSelectedBMO(bmo);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedBMO(null);
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading BMOs...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">BMO Master</h2>
        <Button 
          color="primary" 
          onClick={() => navigate('/backoffice-dashboard/CreateBMO')}
          className="d-flex align-items-center"
        >
          <i className="fas fa-plus me-2"></i>
          ADD +
        </Button>
      </div>
      
      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Card>
        <CardBody>
          <CardTitle tag="h5">
            <Badge color="primary" className="ms-2">
              {bmos.length} Total
            </Badge>
          </CardTitle>
          
          {bmos.length === 0 ? (
            <Alert color="info">
              No BMOs found. Create your first BMO to get started.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>BMO Code</th>
                    <th>BMO Name</th>
                    <th>Address</th>
                    <th>Primary Contact</th>
                    <th>Secondary Contact</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bmos.map((bmo) => (
                    <tr key={bmo.id}>
                      <td>
                        <strong className="text-primary">{bmo.code || '-'}</strong>
                      </td>
                      <td>
                        <strong>{bmo.name}</strong>
                      </td>
                      <td>
                        {bmo.addresslines || '-'}
                      </td>
                      <td>
                        {bmo.primary_representative ? (
                          <span>
                            {bmo.primary_representative.first_name} {bmo.primary_representative.last_name}
                          </span>
                        ) : (
                          <span className="text-muted">Not assigned</span>
                        )}
                      </td>
                      <td>
                        {bmo.secondary_representative ? (
                          <span>
                            {bmo.secondary_representative.first_name} {bmo.secondary_representative.last_name}
                          </span>
                        ) : (
                          <span className="text-muted">Not assigned</span>
                        )}
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle 
                            caret 
                            size="sm" 
                            color="outline-primary"
                          >
                            Action
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem 
                              onClick={() => handleViewBMO(bmo)}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View Details
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* BMO Details Modal */}
      <Modal isOpen={viewModal} toggle={closeViewModal} size="lg" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
        <ModalHeader toggle={closeViewModal} style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          BMO Details
        </ModalHeader>
        <ModalBody style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          {selectedBMO && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>BMO Code:</strong>
                  <div className="text-muted">{selectedBMO.code || 'Not assigned'}</div>
                </Col>
                <Col md={6}>
                  <strong>BMO Name:</strong>
                  <div className="text-muted">{selectedBMO.name}</div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Domain:</strong>
                  <div className="text-muted">{selectedBMO.domain || 'Not specified'}</div>
                </Col>
                <Col md={6}>
                  <strong>Status:</strong>
                  <div>
                    <Badge color={selectedBMO.active ? 'success' : 'warning'}>
                      {selectedBMO.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </Col>
              </Row>

              <hr />
              <h6 className="text-primary">Address Information</h6>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Address Lines:</strong>
                  <div className="text-muted">{selectedBMO.addresslines || 'Not provided'}</div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Suburb:</strong>
                  <div className="text-muted">{selectedBMO.suburb || 'Not provided'}</div>
                </Col>
                <Col md={6}>
                  <strong>City:</strong>
                  <div className="text-muted">{selectedBMO.city || 'Not provided'}</div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Province:</strong>
                  <div className="text-muted">{selectedBMO.province || 'Not provided'}</div>
                </Col>
                <Col md={6}>
                  <strong>Postal Code:</strong>
                  <div className="text-muted">{selectedBMO.postcode || 'Not provided'}</div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Country:</strong>
                  <div className="text-muted">{selectedBMO.country || 'Not provided'}</div>
                </Col>
              </Row>

              <hr />
              <h6 className="text-primary">Representative Information</h6>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Primary Representative:</strong>
                  {selectedBMO.primary_representative ? (
                    <div className="mt-2">
                      <div className="fw-bold">
                        {selectedBMO.primary_representative.first_name} {selectedBMO.primary_representative.last_name}
                      </div>
                      <div className="text-muted">
                        <i className="fas fa-envelope me-1"></i>
                        {selectedBMO.primary_representative.email_address}
                      </div>
                      <div className="text-muted">
                        <i className="fas fa-phone me-1"></i>
                        {selectedBMO.primary_representative.phone_number}
                      </div>
                      {selectedBMO.primary_representative.title && (
                        <div className="text-muted">
                          <i className="fas fa-user-tie me-1"></i>
                          {selectedBMO.primary_representative.title}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted mt-2">Not assigned</div>
                  )}
                </Col>
                <Col md={6}>
                  <strong>Secondary Representative:</strong>
                  {selectedBMO.secondary_representative ? (
                    <div className="mt-2">
                      <div className="fw-bold">
                        {selectedBMO.secondary_representative.first_name} {selectedBMO.secondary_representative.last_name}
                      </div>
                      <div className="text-muted">
                        <i className="fas fa-envelope me-1"></i>
                        {selectedBMO.secondary_representative.email_address}
                      </div>
                      <div className="text-muted">
                        <i className="fas fa-phone me-1"></i>
                        {selectedBMO.secondary_representative.phone_number}
                      </div>
                      {selectedBMO.secondary_representative.title && (
                        <div className="text-muted">
                          <i className="fas fa-user-tie me-1"></i>
                          {selectedBMO.secondary_representative.title}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted mt-2">Not assigned</div>
                  )}
                </Col>
              </Row>

              <hr />
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Created Date:</strong>
                  <div className="text-muted">
                    {selectedBMO.created_at ? new Date(selectedBMO.created_at).toLocaleDateString() : 'Not available'}
                  </div>
                </Col>
                <Col md={6}>
                  <strong>Created By:</strong>
                  <div className="text-muted">
                    {selectedBMO.created_by_name || selectedBMO.created_by?.email || selectedBMO.created_by?.username || 'Not available'}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          <Button color="secondary" onClick={closeViewModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageBMOs;
